// frontend/src/components/cohesion/TourTooltip.tsx
// UX-02 (2026-06 UX audit) — shared custom tooltip for both Joyride tours
// (welcome OnboardingTour + per-surface SurfaceTour).
//
// Adds a "Skip all tips" action next to the regular Skip button. Skip
// dismisses only the current tour; Skip-all sets tours_disabled_at on the
// user's server-side ui_preferences so NO tour ever auto-fires again on
// any device (manual replays from the Help page still work).

import type { CSSProperties } from 'react'
import type { TooltipRenderProps } from 'react-joyride'

interface TourTooltipProps extends TooltipRenderProps {
  t: (key: string) => string
  onSkipAll: () => void
}

const linkBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '6px 4px',
  cursor: 'pointer',
  color: '#6b7280',
  fontSize: 13,
  textDecoration: 'underline',
}

const primaryBtnStyle: CSSProperties = {
  background: '#4b3aa3',
  border: 'none',
  borderRadius: 6,
  padding: '8px 14px',
  cursor: 'pointer',
  color: '#ffffff',
  fontSize: 14,
}

const ghostBtnStyle: CSSProperties = {
  background: 'none',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '8px 12px',
  cursor: 'pointer',
  color: '#1f2329',
  fontSize: 14,
}

export default function TourTooltip(props: TourTooltipProps) {
  const {
    index, size, step, isLastStep,
    backProps, primaryProps, skipProps, tooltipProps,
    t, onSkipAll,
  } = props

  return (
    <div
      {...tooltipProps}
      data-testid="tour-tooltip"
      style={{
        background: '#ffffff',
        color: '#1f2329',
        borderRadius: 8,
        padding: 20,
        maxWidth: 420,
        boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
        textAlign: 'left',
      }}
    >
      {step.title ? (
        <h2 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700 }}>{step.title}</h2>
      ) : null}
      <div style={{ fontSize: 14, lineHeight: 1.5 }}>{step.content}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
        <button type="button" data-testid="tour-skip-all" onClick={onSkipAll} style={linkBtnStyle}>
          {t('tourSkipAll')}
        </button>
        <button type="button" data-testid="tour-skip" {...skipProps} style={linkBtnStyle}>
          {t('skip')}
        </button>
        <span style={{ flex: 1 }} />
        {index > 0 ? (
          <button type="button" data-testid="tour-back" {...backProps} style={ghostBtnStyle}>
            {t('back')}
          </button>
        ) : null}
        <button type="button" data-testid="tour-next" {...primaryProps} style={primaryBtnStyle}>
          {isLastStep ? t('tourFinish') : `${t('next')} (${index + 1}/${size})`}
        </button>
      </div>
    </div>
  )
}
